/**
 * The derivation layer: one folded {@link SessionView} → the workspace a player reads.
 *
 * Everything in this file is a PURE function of server state. The client computes no narration, invents
 * no event and holds no game state of its own (workspace-client.md §3, web-client.md §3) - which is why
 * this is where the interesting rules live and why they are all testable in Node.
 *
 * The two rules this file exists to keep:
 *
 * - **Every message has a sender** (§2m/§2n). There is no code path here that produces a message with
 *   no {@link CastMember}; a system voice that has not been cast simply does not render.
 * - **Nothing is formatted that the server did not send.** A missing field renders as absent, never as
 *   a plausible default. §3's rule - if a field is missing, stop, do not format server-side - applies
 *   to the client too, or the skin quietly becomes the source of truth.
 */
import type { GameActionPayload, GameStatePayload, SeatView, SessionView } from '@core';
import type { ConditionView, GoalView, Outbox, RetroPackage, ScenarioCard } from '@core';
import type { CastMember } from './cast.ts';
import {
  buddy,
  character,
  human,
  monitoringBot,
  stakeholder,
  systemBot,
  voiceSender,
} from './cast.ts';
import { decisionActions, resolveAction } from './affordances.ts';
import { t } from './i18n.ts';
import type { MessageKey } from './i18n.ts';
import { clockOf, dayLabel, deadlineLine, realTimeLabel, workdayOf } from './time.ts';
import type { Workday } from './time.ts';

export const TEAM = 'team';
export const WAR_ROOM = 'war-room';
export const STANDUP = 'standup';
export const DM_BUDDY = 'dm:buddy';

/**
 * A private thread with one colleague (§2c, build step 4). Keyed by SEAT rather than by character
 * name, because the seat is what the ask endpoint takes and a character can be renamed by content
 * without orphaning the conversation.
 */
export function dmOf(seat: string): string {
  return `dm:seat:${seat}`;
}
export const RANDOM = 'random';
export const DECISIONS = 'decisions';
export const PAGE_MONITORING = 'page:monitoring';
export const PAGE_PULSE = 'page:pulse';
export const PAGE_ACCOUNTS = 'page:accounts';

/** One rendered message. `id` is stable across renders so the DOM can be reconciled and e2e can point at it. */
export interface MessageView {
  id: string;
  sender: CastMember;
  text: string;
  /** A second line under the text - a reveal, a caveat, the weight of the card. */
  note: string | null;
  /** The workday this landed on, for the divider. */
  day: Workday;
  /** Game time (§2m): always game, never real. */
  clock: string;
  /** Real time, for the hover title only. Null when the server sent none. */
  realTime: string | null;
  /** `incident` styles the monitoring bot's alarming post; the rest are ordinary. */
  kind: 'chat' | 'incident' | 'beat' | 'narration' | 'buddy' | 'dm' | 'npc-stance';
  /** Reply-buttons that belong to this message (§2b's ladder decides their ceremony). */
  actions: MessageAction[];
  /** Ordering key. Committed messages sort by seq; the ephemeral incident sorts last. */
  order: number;
  /**
   * The committed seq this message IS, when it is one - what a reaction points at (§2i). Null for the
   * things that are not committed messages: the buddy's scripted lines, the ephemeral incident card.
   * Reacting to those would be pointing at nothing.
   */
  seq: number | null;
  /** Reactions somebody has already put on it, in the order they first appeared. */
  reactions: string[];
  /**
   * Reactions YOU just made that the server has not confirmed (the outbox). Drawn beside the committed
   * ones and marked, so the tap lands instantly instead of after a round trip.
   */
  pendingReactions?: { reaction: string; state: 'sending' | 'failed' }[];
  /**
   * Set when this message is one of YOUR OWN, drawn before the server confirmed it (the outbox).
   *
   * Null on everything committed - which is everything anybody else can see. A pending message is
   * local to the person who typed it and is replaced by the real one the moment it commits.
   */
  pending?: { cmdId: string; state: 'sending' | 'failed'; error: string | null } | null;
}

/** A button under a message. Every one of them dispatches a canonical engine input (§3). */
export interface MessageAction {
  id: string;
  label: string;
  /**
   * The input this button sends. Carried HERE rather than rebuilt in the render layer, so that every
   * button's canonical verb is visible in the pure, testable half - which is what lets the
   * "no workspace-only verbs" test see them at all.
   */
  input: GameActionPayload;
  /** How much ceremony this click carries — scaled to the engine's own weight class (§2b). */
  ceremony: 'immediate' | 'confirm' | 'confirm-with-note' | 'promise';
  /** Shown inside the confirm: what this choice is measured against (the A1 readout). */
  stakes: string[];
  odds: string | null;
}

/** One channel, DM or page in the rail. */
export interface ChannelView {
  id: string;
  kind: 'channel' | 'dm' | 'page';
  label: string;
  /** The standing truth pinned at the top: the goal readout in #war-room (§2m "Pin"). */
  topic: string | null;
  /** `alarm` when a lose floor is breached (§2d). */
  tone: 'normal' | 'alarm';
  /** Composer truth (§1a): what THIS channel accepts, in the fiction's voice. Null → no composer. */
  placeholder: string | null;
  emptyText: string;
  messages: MessageView[];
  /** Read-only panels, for the pages that wear a meter's own fiction (§2o). */
  readings: Reading[];
  /**
   * How many messages have landed here since this player last looked (§2a, the corridor). Never causes
   * a switch: tui-layout §3.2's "no panel steals focus" restated as "no channel steals focus".
   */
  unread: number;
  /**
   * The first line of the newest thing said here, dimmed in the rail - peripheral awareness of the room
   * talking, which is the workplace fantasy's texture rather than only UX. Null when nothing is unread.
   *
   * It renders only from THIS player's projection slice, which is true by construction because that is
   * the only data the client has - so a murmur can never leak an asymmetric reveal another seat holds.
   */
  murmur: string | null;
}

/** One number on a page, wearing the fiction that meter actually has in a company (§2o). */
export interface Reading {
  label: string;
  value: string;
  /** `bad` styles it as the graph nobody wants to open. */
  tone: 'good' | 'watch' | 'bad';
  note: string | null;
}

/** A row in the member list: a colleague, never a vacancy (§1b). */
export interface MemberRow {
  id: string;
  who: CastMember;
  role: string;
  /** Live humans only (§1b). NPC presence is authored theatre and is not faked here. */
  presence: 'here' | 'away' | 'gone' | null;
  mine: boolean;
  /** True when nobody human holds it AND the lobby is open — the invitation affordance (§1b). */
  claimable: boolean;
  /** The role's verbs, as the words a colleague would use. */
  does: string[];
  /**
   * Why a table wants this role filled - the deck's `winContribution` (DEC-16). Null when the scenario
   * did not write one; the client never invents a pitch.
   */
  pitch: string | null;
  /** The deck's own recommendation for a first-timer: the seat it refuses to let a bot hold. */
  recommended: boolean;
}

/** What the calendar owes next, in the fiction's words. Null when the engine knows of nothing coming. */
export interface CalendarNote {
  text: string;
  /** True when it opens as this round opens - the strip leans in rather than reminding. */
  imminent: boolean;
}

/**
 * Where the company is in its own story (§2a's time framing; DEC-17 M1).
 *
 * The workspace has always spoken in milestones without having any - "due Thursday" is milestone
 * language, and §2 built the whole fiction on a calendar. This is the calendar finally pointing at
 * something the engine holds: the stretch the team is in, and the one they are working toward.
 */
export interface MilestoneNote {
  /** The current milestone in the deck's own words. */
  current: string;
  /** The next gate's name, or null on the last one - a company with nothing ahead of it is at the end. */
  next: string | null;
  /**
   * WHAT THE NEXT GATE TAKES, in the cast's own words - or null when the deck authored no condition.
   *
   * The strip used to name the gate and stop, which told a player where they were going and nothing
   * whatsoever about how to get there. The engine has always known: `goal.advance.condition` arrives
   * decomposed into clauses with their current readings, evaluated against the same scope the sweep
   * reads. It reached this client and nothing rendered it - so "next: Resolve" was a signpost with no
   * distance on it.
   */
  nextNeeds: string | null;
  /** What crossing the next gate makes reachable, so a locked ceremony reads as "not yet". */
  unlocks: string[];
}

/** A colleague visibly composing a line (§2j: latency is characterization, not load). */
export interface TypingRow {
  who: CastMember;
  /** Which channel they are typing into, so the indicator sits in the room it belongs to. */
  channel: string;
}

/** One call, as the all-hands lays it out (§2b/§2f). */
export interface FinaleDecision {
  id: string;
  chose: string;
  /** "alone, over two objections" - the path in words, or null when there is nothing notable to say. */
  how: string | null;
  /** What the table wrote at the time, verbatim. The teaching payload (§2b). */
  quotes: { who: string; text: string }[];
}

/**
 * The ending, once there is one (§2f).
 *
 * Null for the whole session and then present, which is the point: this is the one moment that is NOT
 * another day at work, and the client should not be carrying an ending around while people are working.
 */
export interface Finale {
  /**
   * What the engine settled. THREE values since DEC-17 M4: the endless frame ends in `collapse`, which
   * is a different fact from losing - the company is gone rather than the target missed - and the
   * engine now says so with its own outcome member. The four-way spectrum (endings-and-failure §4 -
   * triumph, diminished, pyrrhic) is still R24 and still deferred; what changed is that the game DOES
   * make this distinction now, so rendering it is staging rather than inventing (§2f).
   */
  outcome: 'win' | 'lose' | 'collapse';
  /** The A5 slide line, in the engine's own words. */
  headline: string;
  score: number | null;
  /**
   * What that score is MADE OF, at a collapse (DEC-17 M4 §3). Null in every other ending, because only
   * the endless frame has a survival score to break down. One number hides four achievements, and the
   * team is going to want to argue about which of the four they were bad at.
   */
  survival: {
    final: number;
    peak: number;
    rounds: number;
    gates: number;
  } | null;
  /** The retro story as SHORT PAGES the team can move through (§2j: posts, not an essay). */
  pages: string[];
  /** The fidelity badge, carried INTO the all-hands (§2f: the worst place to start pretending). */
  source: 'llm' | 'scripted' | null;
  decisions: FinaleDecision[];
  /** Your own slice, addressed to you and rendered on your screen only. */
  letter: string | null;
}

export interface WorkspaceModel {
  name: string;
  /** The engine's round. The promise form needs it: a due date is a ROUND, not a workday. */
  round: number;
  /** The ending, or null while the day is still running (§2f). */
  finale: Finale | null;
  /** The workspace before day one (§2l): people trickle in, chat, and take roles. */
  lobby: boolean;
  started: boolean;
  day: Workday;
  channels: ChannelView[];
  /** The calendar strip (§2a). Null rather than "nothing scheduled", which is a reminder about nothing. */
  calendar: CalendarNote | null;
  /** The milestone chain (DEC-17 M1). Null when the deck is a single stretch with no gates to cross. */
  milestone: MilestoneNote | null;
  /**
   * Who is composing right now (§2j). A careful NPC visibly typing for four seconds reads as
   * personality; the same four seconds of nothing reads as a hung app. Only ever what the server says
   * is happening - this client never invents a typist to fill a pause.
   */
  typing: TypingRow[];
  members: MemberRow[];
  you: {
    seat: string | null;
    /** True once this seat has closed its day — presence greys out (§2m "Sign-off"). */
    signedOff: boolean;
    verbs: string[];
  };
}

/**
 * How far this player has read in each channel (§2a, the corridor; §2r's "new since you left").
 *
 * Client-local and deliberately so: read state as something CHARACTERS can cite is §2i's `messageSeen`
 * seam, which is not built - so nothing here reaches the wire, and no NPC may reference it. What it
 * drives is the badge, the murmur and the divider, all of which are this reader's own business.
 */
export class ReadMarks {
  private readonly seen = new Map<string, number>();

  /** Marks a channel read up to its newest message. */
  read(channel: string, newest: number): void {
    this.seen.set(channel, Math.max(this.seen.get(channel) ?? 0, newest));
  }

  /** The order this channel was last read to, or null when it has never been opened. */
  markOf(channel: string): number | null {
    return this.seen.get(channel) ?? null;
  }
}

/**
 * Which round a committed message landed in.
 *
 * The wire does not carry a round on the envelope, and the day a message belongs to is not derivable
 * from its real timestamp without a wall clock - which is exactly what §2's time rule forbids. So the
 * client records the round it saw at each seq as deltas arrive, and reads it back here.
 *
 * The honest limitation, stated rather than hidden: messages that were committed BEFORE this client
 * connected fall into the earliest round it observed. They are in the right order and under a plausible
 * day, and a replayed conversation can show one divider in the wrong place. Fixing it properly is a
 * `round` on the envelope - recorded in workspace-client.md §4a rather than invented here.
 */
export class RoundLog {
  private readonly marks: { seq: number; round: number }[] = [];

  /** Records the round in force at the latest applied seq. Idempotent per (seq, round). */
  observe(seq: number, round: number): void {
    const last = this.marks[this.marks.length - 1];
    if (last && last.seq === seq && last.round === round) {
      return;
    }
    if (last && last.round === round) {
      last.seq = seq;
      return;
    }

    this.marks.push({ seq, round });
  }

  /** The round a message at `seq` belongs to, or `fallback` when nothing has been observed. */
  roundAt(seq: number, fallback: number): number {
    for (const mark of this.marks) {
      if (seq <= mark.seq) {
        return mark.round;
      }
    }

    return this.marks.length ? this.marks[this.marks.length - 1]!.round : fallback;
  }
}

// ---- the goal readout, which is the topic line (§2d/§2o) -----------------------------------------

const METRIC_KEYS: Record<string, MessageKey> = {
  stability: 'metric.stability',
  sev: 'metric.sev',
  trust: 'metric.trust',
  morale: 'metric.morale',
  quality: 'metric.quality',
  scope: 'metric.scope',
};

/**
 * The cast's own word for a meter.
 *
 * Composed scenarios namespace their meters (`acme.trust`), so the tail is tried after the whole key -
 * without it the topic line read "acme.trust at 50, we need 60", which is a metric identifier in a
 * sentence a colleague is supposed to have said (§2g, caught by looking at the screen rather than at
 * the catalog). An unmapped key is HUMANISED rather than invented: "delivery_speed" becomes "delivery
 * speed", which is at worst plain and at best exactly right, and never a name the content never wrote.
 */
/**
 * How one deck reads in the "start a team" picker (DEC-17 M4 increment 8b).
 *
 * The FRAME phrases it, because the two are genuinely different invitations rather than two lengths of
 * the same one: an excerpt drops you into a situation already underway, a sim hands you a company to
 * run until it stops. And it says DAYS, not rounds - §2g's language law applies to a menu as much as
 * to a message.
 */
export function deckOffer(deck: ScenarioCard): string {
  return deck.frame === 'sim'
    ? t('app.deckSim', { title: deck.title })
    : t('app.deckExcerpt', { title: deck.title, days: deck.deadline ?? 0 });
}

/**
 * The `@`-token being typed at the caret, or null when the composer is not in a mention.
 *
 * Anywhere in the line, not only at the start: "@founder are you here?" is the common shape, and so is
 * mentioning somebody mid-sentence. A token ends at whitespace, so once you have typed a space the
 * mention is finished and the picker closes on its own.
 */
export function mentionQuery(text: string, caret: number): string | null {
  const head = text.slice(0, Math.max(0, caret));
  const at = head.lastIndexOf('@');
  if (at < 0) {
    return null;
  }

  // An `@` must start a word - `a@b` is an address, not a mention.
  const before = at === 0 ? '' : head[at - 1]!;
  if (before && !/\s/.test(before)) {
    return null;
  }

  const token = head.slice(at + 1);
  return /\s/.test(token) ? null : token;
}

/**
 * Who a prefix could mean (§3.5: a mention is addressed to a SEAT, and the seat is what the server
 * parses). Matched on the seat key or on any word of the person's name, so "@dan" finds Daniel and
 * "@comms" finds the chair he sits in.
 *
 * Yourself is excluded: a mention is how you get somebody else's attention, and the one person already
 * reading the room is you.
 */
export function mentionCandidates(members: readonly MemberRow[], prefix: string): MemberRow[] {
  const needle = prefix.trim().toLowerCase();
  return members.filter((m) => {
    if (m.mine) {
      return false;
    }

    if (needle.length === 0) {
      return true;
    }

    // The SEAT is what the server parses, and on a MemberRow that is `role` - `id` identifies the row,
    // not the chair. Both are matched anyway, because a player typing `@` is aiming at a person and
    // should not have to know which of the two words the client happens to key on.
    return (
      mentionSeat(m).toLowerCase().startsWith(needle) ||
      m.id.toLowerCase().startsWith(needle) ||
      m.who.name
        .toLowerCase()
        .split(/\s+/)
        .some((word) => word.startsWith(needle))
    );
  });
}

/** The seat key a mention should carry for this row - what `@` addresses and the server parses. */
export function mentionSeat(member: MemberRow): string {
  return member.role || member.id;
}

/** Replaces the `@`-token at the caret with a chosen seat, and says where the caret lands. */
export function applyMention(
  text: string,
  caret: number,
  seat: string,
): { text: string; caret: number } {
  const head = text.slice(0, Math.max(0, caret));
  const at = head.lastIndexOf('@');
  if (at < 0) {
    return { text, caret };
  }

  // A trailing space, because the next thing you type is the sentence rather than more of the name.
  const inserted = `@${seat} `;
  return { text: text.slice(0, at) + inserted + text.slice(caret), caret: at + inserted.length };
}

export function metricName(metric: string): string {
  const key = METRIC_KEYS[metric] ?? METRIC_KEYS[metric.slice(metric.lastIndexOf('.') + 1)];
  if (key) {
    return t(key);
  }

  return metric.slice(metric.lastIndexOf('.') + 1).replace(/[-_]/g, ' ');
}

function clauseLine(clause: ConditionView['clauses'][number], survival: boolean): string {
  const current = clause.currentText ?? (clause.current === null ? '?' : String(clause.current));
  const target = clause.targetText ?? (clause.target === null ? '?' : String(clause.target));
  const metric = metricName(clause.metric);
  const params = { metric, current, target };

  switch (clause.op) {
    case 'gt':
    case 'gte':
      return t(survival ? 'topic.clauseBelow' : 'topic.clauseAbove', params);
    case 'lt':
    case 'lte':
      return t(survival ? 'topic.clauseKeepUnder' : 'topic.clauseUnder', params);
    default:
      return t('topic.clauseAt', params);
  }
}

function conditionLine(condition: ConditionView, survival: boolean): string {
  const join = condition.join === 'or' ? t('topic.or') : t('topic.and');
  return condition.clauses.map((c) => clauseLine(c, survival)).join(join);
}

/**
 * The A1 readout as a channel topic (§2d): what winning means, what we are holding, and the clock. A
 * breached floor takes the whole line - the failure is the news, and burying it under the win condition
 * is the dishonesty the section exists to forbid.
 */
export function topicLine(goal: GoalView | null | undefined, day: Workday): string | null {
  if (!goal) {
    return null;
  }

  const breached = goal.lose.find((l) => l.met);
  if (breached) {
    return `${t('topic.goalBreached', { lose: conditionLine(breached, false) })} · ${deadlineLine(day)}`;
  }

  const win = goal.win.map((w) => conditionLine(w, false)).join(t('topic.or'));
  const holds = goal.lose.map((l) => conditionLine(l, true)).join(t('topic.and'));
  const line = holds ? t('topic.goalHold', { win, lose: holds }) : t('topic.goalGood', { win });
  return `${line} · ${deadlineLine(day)}`;
}

/** True when any lose floor is standing on the wrong side of its line — the channel goes to alarm. */
export function breached(goal: GoalView | null | undefined): boolean {
  return Boolean(goal?.lose.some((l) => l.met));
}

/** The stakes shown inside a Consequence-tier confirm: the same A1 clauses, one line each (§2b). */
export function stakesFor(goal: GoalView | null | undefined): string[] {
  if (!goal) {
    return [];
  }

  return [
    ...goal.win.map((w) => conditionLine(w, false)),
    ...goal.lose.map((l) => conditionLine(l, true)),
  ];
}

// ---- the beat table: which committed inputs become messages, and from whom ------------------------

interface BeatRender {
  channel: string;
  key: MessageKey;
  /** How to fill the line's `{…}` from the beat. */
  params: (ctx: BeatContext) => Record<string, string>;
}

interface BeatContext {
  actorName: string;
  day: Workday;
}

/**
 * ONE table, for the same reason the terminal has one command catalog: a beat that is not in here does
 * not render as a message, and there is nowhere else in the render layer to add one. Its narration and
 * the NPC lines attached to it still land (they carry real senders), which is what keeps an unmapped
 * input honest rather than invisible.
 */
const BEATS: Record<string, BeatRender> = {
  claimSeat: { channel: TEAM, key: 'beat.joined', params: (c) => ({ name: c.actorName }) },
  startSession: { channel: TEAM, key: 'beat.dayOpen', params: (c) => ({ day: dayLabel(c.day) }) },
  checkIn: { channel: TEAM, key: 'beat.signedOff', params: (c) => ({ name: c.actorName }) },
};

// ---- assembling the workspace --------------------------------------------------------------------

/**
 * The one place a player id becomes a person.
 *
 * The id arrives spelled two ways: the roster carries it whole (`evt.session.memberJoined`), the
 * projection shortens it (`seats[].player`). Exact match first, prefix match second - and the prefix
 * match counts, because ids are uuidv7 and two players who joined in the same second share their first
 * eight characters. An ambiguous prefix resolves to NOBODY, which renders as the short id: an
 * unhelpful row beats a row that confidently puts one player's name on another player's desk.
 */
function memberOf(view: SessionView, playerId: string) {
  const exact = view.members.find((m) => m.playerId === playerId);
  if (exact) {
    return exact;
  }

  const candidates = view.members.filter((m) => m.playerId.startsWith(playerId));
  return candidates.length === 1 ? candidates[0] : undefined;
}

function nameOf(view: SessionView, playerId: string): string {
  const named = memberOf(view, playerId)?.displayName;
  return named && named.length > 0 ? named : playerId.slice(0, 8);
}

function senderForPlayer(view: SessionView, playerId: string): CastMember {
  return human(playerId, nameOf(view, playerId));
}

/** The colleague who holds a seat, or null when the scenario authored none for that role. */
function seatCharacter(seat: SeatView | undefined): CastMember | null {
  return seat?.character ? character(seat.character, roleName(seat.key)) : null;
}

/**
 * How this call gets made, in the owner's own words (§2b). The style is a RULE - the engine refuses a
 * resolve that breaks it - and stating it on the card is what makes the buttons underneath legible
 * rather than arbitrary.
 */
function styleNote(decision: GameStatePayload['decisions'][number]): string | null {
  switch (decision.style) {
    case 'OwnerDecides':
      return t('decision.style.OwnerDecides');
    case 'ConsultThenDecide':
      return t('decision.style.ConsultThenDecide', { n: String(decision.requiredConsults) });
    case 'Committee':
      return t('decision.style.Committee', {
        votes: String(decision.votes),
        quorum: String(decision.quorum),
      });
    case 'ApprovalChain':
      return t('decision.style.ApprovalChain', { approver: decision.approver ?? '?' });
    default:
      return null;
  }
}

/**
 * The ending, assembled from committed facts only (§2f: "staged, not invented").
 *
 * Every field here comes from something the server settled - the engine's outcome, the retro package it
 * froze, the prose it published, the private slice it wrote for this player. The client chooses the
 * ARRANGEMENT and nothing else.
 */
function finaleOf(
  view: SessionView | undefined,
  game: GameStatePayload | null,
  seat: string | null,
): Finale | null {
  const outcome = game?.outcome;
  if (!game || (outcome !== 'Win' && outcome !== 'Lose' && outcome !== 'Collapse')) {
    return null;
  }

  const doc = view?.retro ?? null;
  const pack = doc?.package ?? null;

  const ending = outcome === 'Win' ? 'win' : outcome === 'Collapse' ? 'collapse' : 'lose';
  const fallback =
    ending === 'win'
      ? 'finale.wonPlain'
      : ending === 'collapse'
        ? 'finale.collapsedPlain'
        : 'finale.lostPlain';

  return {
    outcome: ending,
    // The engine's own one-line summary. A5's ending moment is this sentence on a slide.
    headline: pack?.headline ?? t(fallback),
    score: pack?.score ?? null,
    survival: pack?.survival
      ? {
          final: pack.survival.final,
          peak: pack.survival.peak,
          rounds: pack.survival.rounds,
          gates: pack.survival.gates,
        }
      : null,
    // §2j: the story arrives as one block of prose and the all-hands pages through it. Split on the
    // blank lines the template already writes between its sections, so the pages are the AUTHOR'S
    // paragraphs rather than a length the client picked.
    pages: (doc?.text ?? '')
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0),
    source: doc?.source ?? null,
    decisions: (pack?.decisions ?? []).map((d) => ({
      id: d.id,
      chose: d.chose,
      how: pathOf(d),
      // Names, never ids (§2k): this screen is meant to be screenshotted and shared.
      quotes: d.rationales.map((r) => ({
        who: r.seat ? roleName(r.seat, seatTitle(game, r.seat)) : t('finale.theRoom'),
        text: r.text,
      })),
    })),
    // Yours only. The server writes one per player and serves each player their own; the client never
    // holds anyone else's, so there is nothing here to leak.
    letter: view?.personalRetro?.seat === seat ? (view?.personalRetro?.text ?? null) : null,
  };
}

/** The path a call took, in words - or null when there is nothing worth saying about it. */
function pathOf(d: NonNullable<RetroPackage['decisions']>[number]): string | null {
  if (d.autoResolved) {
    // The most retro-worthy path of all: nobody decided, the clock did.
    return t('finale.pathClock');
  }

  if (d.overrode.length) {
    return t('finale.pathOverrode', { n: String(d.overrode.length) });
  }

  if (d.deferrals > 0) {
    return t('finale.pathDeferred', { n: String(d.deferrals) });
  }

  return null;
}

function seatTitle(game: GameStatePayload, seat: string): string | null {
  const s = game.seats.find((x) => x.key === seat);
  return s?.character ?? s?.title ?? null;
}

/**
 * What to call a role. The AUTHORED title first (DEC-16): decks name their roles, and that name reached
 * no client until the picker needed it - so "incident-lead" was being shown to players who had never
 * read the repo. De-hyphenating the key stays as the fallback for a deck that named nothing.
 */
function roleName(key: string, title?: string | null): string {
  return title?.trim() ? title : key.replace(/[-_]/g, ' ');
}

interface Draft {
  id: string;
  /** The committed seq this draft IS, when it is one - what a reaction can point at (§2i). */
  seq?: number;
  sender: CastMember;
  text: string;
  note: string | null;
  channel: string;
  round: number;
  order: number;
  realTime: string | null;
  kind: MessageView['kind'];
  actions: MessageAction[];
}

/** Turns drafts into rendered messages: day dividers and clocks come from position, never from a wall clock. */
function place(
  drafts: Draft[],
  deadline: number | null,
  reactions: Record<number, string[]>,
): Map<string, MessageView[]> {
  const byChannel = new Map<string, MessageView[]>();
  const sorted = [...drafts].sort((a, b) => a.order - b.order);

  // The clock a line carries is its position within its DAY, so a busy day fills up towards the
  // evening and a quiet one stays near the morning. Counted per channel-day, which is what makes each
  // channel read as its own conversation rather than as a slice of one global feed.
  const seen = new Map<string, number>();
  for (const d of sorted) {
    const bucket = `${d.channel}/${d.round}`;
    const index = seen.get(bucket) ?? 0;
    seen.set(bucket, index + 1);

    const day = workdayOf(d.round, deadline);
    const list = byChannel.get(d.channel) ?? [];
    list.push({
      id: d.id,
      sender: d.sender,
      text: d.text,
      note: d.note,
      day,
      clock: clockOf(index),
      realTime: d.realTime,
      kind: d.kind,
      actions: d.actions,
      order: d.order,
      seq: d.seq ?? null,
      reactions: d.seq ? (reactions[d.seq] ?? []) : [],
    });
    byChannel.set(d.channel, list);
  }

  return byChannel;
}

/**
 * The §1a buddy DM: the first thing rendered, four lines, in character, before any channel. Scripted -
 * no model call - so it survives a dead provider and costs nothing.
 */
function buddyDrafts(workspaceName: string, seated: boolean, started: boolean): Draft[] {
  const who = buddy();
  const lines: MessageKey[] = [
    'buddy.line1',
    'buddy.line2',
    'buddy.line3',
    seated ? 'buddy.line4Seated' : 'buddy.line4',
  ];
  const drafts: Draft[] = lines.map((key, i) => ({
    id: `buddy-${i}`,
    sender: who,
    text: t(key, { workspace: workspaceName }),
    note: null,
    channel: DM_BUDDY,
    round: 0,
    order: -1000 + i,
    realTime: null,
    kind: 'buddy' as const,
    actions: [],
  }));

  if (started) {
    drafts.push({
      id: 'buddy-started',
      sender: who,
      text: t('buddy.started'),
      note: null,
      channel: DM_BUDDY,
      round: 0,
      order: -900,
      realTime: null,
      kind: 'buddy',
      actions: [],
    });
  }

  return drafts;
}

/**
 * The incident, as the monitoring bot posts it (§2n), with the options as replies and their ceremony
 * scaled to the engine's own weight class (§2b).
 *
 * The event is EPHEMERAL - it rides `stream.state.delta`, not the seq log - so it sorts last and has no
 * real timestamp. That is the truth about it, and it is rendered as the truth rather than dressed up
 * with an invented clock.
 */
function incidentDraft(
  game: GameStatePayload,
  goal: GoalView | null | undefined,
  seated: boolean,
): Draft | null {
  const event = game.activeEvent;
  if (!event) {
    return null;
  }

  const heavy = event.tier === 'consequence' || event.tier === 'scripted';
  const stakes = heavy ? stakesFor(goal) : [];

  const actions: MessageAction[] = seated
    ? event.options.map((o) => ({
        id: o.id,
        label: o.label ?? t('incident.unlabelled'),
        input: resolveAction(event.id, o.id),
        // Texture and flavour are small, and treating them as small is honest. Consequence and scripted
        // open a confirm that shows the trade; a bet is always worth a second look, whatever its tier,
        // because §9.7 says a gamble must be priced where it is taken.
        ceremony: heavy ? 'confirm-with-note' : o.isBet ? 'confirm' : 'immediate',
        stakes,
        odds: o.odds ?? null,
      }))
    : [];

  // One line, and the ORDER is the priority: what only you can see, then the move you are missing, then
  // the card's own weight. standards/messages.md - a reader with no way to act needs the way to act
  // before they need the colour commentary.
  const note = event.reveal
    ? t('incident.onlyYou', { reveal: event.reveal })
    : !seated
      ? t('incident.needRole')
      : event.tier === 'consequence'
        ? t('incident.caused')
        : event.tier === 'scripted'
          ? t('incident.scripted')
          : null;

  return {
    id: `incident-${event.id}`,
    sender: monitoringBot(),
    text: event.text ?? event.id,
    note,
    channel: WAR_ROOM,
    round: game.round,
    order: Number.MAX_SAFE_INTEGER,
    realTime: null,
    kind: 'incident',
    actions,
  };
}

/**
 * The digest, in the words a colleague would use (§2g). Counts only, from the projection's own numbers -
 * this is the templated tier the engine always had, and it is what the digest bot reads out when the
 * model is not writing prose.
 */
function digestLines(digest: GameStatePayload['digest']): string {
  if (digest.quiet) {
    return t('standup.digestQuiet');
  }

  const parts: string[] = [];
  const add = (n: number, key: MessageKey): void => {
    if (n > 0) parts.push(t(key, { n }));
  };
  add(digest.events, 'standup.digestEvents');
  add(digest.decisions, 'standup.digestDecisions');
  add(digest.decisionsTimedOut, 'standup.digestTimedOut');
  add(digest.workCompleted, 'standup.digestWork');
  add(digest.fuses, 'standup.digestFuses');
  add(digest.concernsEscalated, 'standup.digestEscalated');
  add(digest.promisesBroken, 'standup.digestPromises');
  add(digest.hires, 'standup.digestHires');
  return parts.length ? parts.join(' · ') : t('standup.digestQuiet');
}

/**
 * The standup as a thread that OPENS ITSELF (§2, the Offered row).
 *
 * The Offered contract maps onto a thread exactly: it exists, it demands nothing, and "taking it" is
 * replying. So the meeting bot posts the digest as the thread's first message when the calendar opens
 * the scene, the beat prompt says what this part of the meeting is for, and the reply IS the move.
 *
 * Nothing here is invented: the digest numbers, the beat and its index all come off the projection. When
 * no scene is running there is no thread, because there is no meeting.
 */
function standupDrafts(game: GameStatePayload, seated: boolean): Draft[] {
  const scene = game.scene;
  if (!scene) {
    return [];
  }

  const bot = systemBot('meeting');
  const drafts: Draft[] = [
    {
      id: `standup-open-${game.round}`,
      sender: bot,
      // An OFFERED meeting is an invitation that constrains nothing (first-run-review §A3), and the
      // client says so rather than showing a meeting that appears to have started without anybody.
      text: scene.offered
        ? t('standup.opened', { scene: sceneName(scene.kind) })
        : t('standup.running', { scene: sceneName(scene.kind) }),
      note: digestLines(game.digest),
      channel: STANDUP,
      round: game.round,
      order: Number.MAX_SAFE_INTEGER - 20,
      realTime: null,
      kind: 'narration',
      actions: [],
    },
    {
      id: `standup-beat-${game.round}-${scene.beatIndex}`,
      sender: bot,
      text: beatPrompt(scene.beat),
      note: t('standup.beatOf', { index: scene.beatIndex + 1, count: scene.beatCount }),
      channel: STANDUP,
      round: game.round,
      order: Number.MAX_SAFE_INTEGER - 19,
      realTime: null,
      kind: 'narration',
      actions: seated
        ? [
            {
              id: 'advance',
              label: scene.offered ? t('standup.take') : t('standup.next'),
              // Taking an offered meeting and moving it on are the same canonical verb - the terminal
              // spells both `/beat`. The LABEL differs because the two moments feel different; the
              // input may not, or this client would have grown a verb of its own.
              input: { kind: 'advanceBeat' },
              ceremony: 'immediate',
              stakes: [],
              odds: null,
            },
          ]
        : [],
    },
  ];

  return drafts;
}

/** The scene's own name, in the fiction's words. An unmapped kind keeps its key rather than inventing one. */
function sceneName(kind: string): string {
  const key = SCENE_NAMES[kind];
  return key ? t(key) : kind;
}

const SCENE_NAMES: Record<string, MessageKey> = {
  Standup: 'scene.standup',
  Planning: 'scene.planning',
  Governance: 'scene.governance',
  Retro: 'scene.retro',
};

/** What this part of the meeting is for. The engine's beat name, as a sentence somebody would say. */
function beatPrompt(beat: string): string {
  const key = BEAT_PROMPTS[beat];
  return key ? t(key) : beat;
}

const BEAT_PROMPTS: Record<string, MessageKey> = {
  digest: 'standup.beatDigest',
  reveals: 'standup.beatReveals',
  agenda: 'standup.beatAgenda',
  commitments: 'standup.beatCommitments',
  close: 'standup.beatClose',
};

function readingsFor(game: GameStatePayload | null, page: string): Reading[] {
  if (!game) {
    return [];
  }

  const meters = game.meters ?? {};
  const has = (k: string): boolean => typeof meters[k] === 'number';
  const num = (k: string): number => meters[k] as number;

  if (page === PAGE_MONITORING) {
    const readings: Reading[] = [];
    if (has('stability')) {
      const v = num('stability');
      readings.push({
        label: t('page.monitoringStability'),
        value: `${v}`,
        tone: v >= 70 ? 'good' : v >= 40 ? 'watch' : 'bad',
        note: null,
      });
    }
    if (has('sev')) {
      const v = num('sev');
      readings.push({
        label: t('page.monitoringSev'),
        value: v <= 0 ? '—' : `sev${v}`,
        tone: v <= 0 ? 'good' : v <= 2 ? 'watch' : 'bad',
        note: null,
      });
    }

    // WHAT IS ARMED (DEC-17 M2). Monitoring is where a pending problem belongs - this page is "what the
    // graphs say right now", and "the platform drops when quality goes under 45" is exactly the alert a
    // real monitoring tool would be showing. It is the cheapest dread this medium sells: a number that
    // is fine, next to the sentence that says what would stop it being fine.
    //
    // Rendered through the same `conditionLine` the topic uses, so §2g's language law is enforced in
    // one place and a raw comparison never reaches a player.
    for (const fuse of game.gauges?.armed ?? []) {
      readings.push({
        label: t('page.monitoringArmed', { metric: metricName(fuse.metric) }),
        value: fuse.condition
          ? conditionLine(fuse.condition, false)
          : t('page.monitoringArmedUnknown'),
        // Already true means it lands on the next tick - that is not a watch, it is a warning.
        tone: fuse.condition?.met ? 'bad' : 'watch',
        note: null,
      });
    }

    return readings;
  }

  if (page === PAGE_PULSE) {
    if (!has('morale')) {
      return [];
    }
    const v = num('morale');
    const answered = game.seats.filter((s) => s.checkedIn).length;
    return [
      {
        label: t('page.pulseScore'),
        value: `${v}`,
        tone: v >= 60 ? 'good' : v >= 35 ? 'watch' : 'bad',
        note: t('page.pulseParticipation', { done: answered, total: game.seats.length }),
      },
    ];
  }

  if (page === PAGE_ACCOUNTS) {
    if (!has('trust')) {
      return [];
    }
    const v = num('trust');
    const tone = v >= 60 ? 'good' : v >= 30 ? 'watch' : 'bad';
    return [
      {
        label: t('page.accountsCustomer'),
        value:
          tone === 'good'
            ? t('page.accountHealthy')
            : tone === 'watch'
              ? t('page.accountWatch')
              : t('page.accountAtRisk'),
        tone,
        // game-loop §9.6's asymmetric rates finally read naturally here instead of as a trust line chart.
        note: tone === 'good' ? null : t('page.rebuildingSlow'),
      },
    ];
  }

  return [];
}

/** The one entry point: everything the workspace renders, derived from the folded session. */
export function buildWorkspace(
  view: SessionView | undefined,
  playerId: string | null,
  rounds: RoundLog,
  marks: ReadMarks = new ReadMarks(),
  outbox?: Outbox,
  asks: readonly { seat: string; text: string }[] = [],
): WorkspaceModel {
  const game = view?.game ?? null;
  const deadline = game?.deadline ?? null;
  const round = game?.round ?? 0;
  const day = workdayOf(round, deadline);
  const started = Boolean(game?.started);
  const name = view?.meta.name || t('app.workspaceFallback');

  const mySeat = game?.seats.find((s) => s.mine) ?? null;
  const seated = Boolean(mySeat);
  const signedOff = Boolean(mySeat?.checkedIn);

  const drafts: Draft[] = [...buddyDrafts(name, seated, started)];

  // Chat: exactly as the terminal reads it, from the same committed events.
  for (const line of view?.chat ?? []) {
    drafts.push({
      id: `chat-${line.seq}`,
      sender: senderForPlayer(view!, line.playerId),
      text: line.text,
      note: null,
      // The room it was TYPED in (register item 16), not the one room the wire used to have. `null`
      // is every line committed before item 16 and every line from a client that does not model
      // rooms - those belong in #team, which is where they have always rendered, so a replayed
      // session looks exactly as it did.
      channel: line.channel ?? TEAM,
      round: rounds.roundAt(line.seq, round),
      order: line.seq,
      seq: line.seq,
      realTime: realTimeLabel(line.ts),
      kind: 'chat',
      actions: [],
    });
  }

  // The action feed: the mapped beats become messages; narration and NPC speech attached to ANY beat
  // land too, because those carry senders of their own.
  for (const beat of view?.feed ?? []) {
    const beatRound = rounds.roundAt(beat.seq, round);
    const render = BEATS[beat.kind];
    const actorName = beat.actor ? nameOf(view!, beat.actor) : t('app.you');

    if (render) {
      drafts.push({
        id: `beat-${beat.seq}`,
        // A beat with an actor is that PERSON's; one without is the workspace's own bookkeeping, which
        // is the digest bot's job (§2n). Neither is a narrator.
        sender: beat.actor ? senderForPlayer(view!, beat.actor) : systemBot('digest'),
        text: t(render.key, render.params({ actorName, day: workdayOf(beatRound, deadline) })),
        note: null,
        channel: render.channel,
        round: beatRound,
        order: beat.seq,
        realTime: null,
        kind: 'beat',
        actions: [],
      });
    }

    if (beat.narration) {
      // ONE post per message (§2j's beat-split), and the split is the SERVER'S: the committed text is
      // already short, and a client that chopped it here would chop differently from the terminal.
      // The sender is the server's too (§2n) - this used to hardcode the deploy bot, so an incident
      // arriving was announced by the thing that ships code.
      const sender = voiceSender(beat.narration.voice);
      const posts = beat.narration.posts?.length ? beat.narration.posts : [beat.narration.text];
      posts.forEach((post, i) => {
        drafts.push({
          id: `narration-${beat.seq}-${i}`,
          sender,
          text: post,
          note: null,
          channel: render?.channel ?? WAR_ROOM,
          round: beatRound,
          // Fractional order keeps the posts in the author's sequence without colliding with the beat
          // they belong to or with the scene line that follows.
          order: beat.seq + 0.1 + i * 0.001,
          realTime: null,
          kind: 'narration',
          actions: [],
        });
      });
    }

    if (beat.scene) {
      // A scene being read out loud is MEETING NOTES, posted into the room the meeting happened in.
      // The first build folded this into the narrator and then never rendered it at all.
      const scenePosts = beat.scene.posts?.length ? beat.scene.posts : [beat.scene.text];
      scenePosts.forEach((post, i) => {
        drafts.push({
          id: `scene-${beat.seq}-${i}`,
          sender: voiceSender(beat.scene!.voice ?? 'meeting'),
          text: post,
          note: null,
          channel: STANDUP,
          round: beatRound,
          order: beat.seq + 0.15 + i * 0.001,
          realTime: null,
          kind: 'narration',
          actions: [],
        });
      });
    }

    for (const [i, said] of beat.npcLines.entries()) {
      // A line can come from a SEAT or from the outside world (§7.5b): `evt.npc.said` carries the key,
      // and stakeholders are not seats. Checked in that order because the seat roster is the common
      // case, and falling through to the roster is what makes acme render as acme rather than vanish.
      const outside = game?.stakeholders?.find((s) => s.key === said.seat);
      const who =
        seatCharacter(game?.seats.find((s) => s.key === said.seat)) ??
        (outside ? stakeholder(outside.key, outside.name) : null);
      if (!who) {
        // No cast identity, no message (§2n). A line from an unnamed seat would be a UI panel in a
        // message costume, which is precisely what this rule forbids.
        continue;
      }

      // THE WATERCOOLER (§2h). A banter line rides the round-close tick, so the beat that closed a
      // round is the one carrying off-duty talk - and off-duty talk does not belong in the room where
      // the incident is. Matched by the beat's own emitted kinds, the same way stances are: nothing
      // about the association is guesswork.
      const offDuty = beat.emitted.some((e) => e.kind === 'round-closed');

      drafts.push({
        id: `said-${beat.seq}-${i}`,
        sender: who,
        text: said.text,
        note: null,
        channel: offDuty ? RANDOM : (render?.channel ?? WAR_ROOM),
        round: beatRound,
        order: beat.seq + 0.2 + i / 100,
        seq: beat.seq,
        realTime: null,
        kind: 'chat',
        // Answering the outside world with a commitment (§7.5b). The reply-to belongs HERE, on the
        // message that provoked it, because that is what a promise is: a thing you say back to
        // somebody who just asked. A colleague's line gets no such button - you do not promise a
        // teammate through the outside-world ledger.
        actions:
          outside && seated
            ? [
                {
                  id: `promise:${outside.key}:${beat.seq}`,
                  label: t('promise.offer', { who: who.name }),
                  input: {
                    kind: 'makePromise',
                    stakeholder: outside.key,
                    content: '',
                    dueRound: 0,
                  },
                  ceremony: 'promise',
                  stakes: [],
                  odds: null,
                },
              ]
            : [],
      });
    }
  }

  // AN ANSWER TO A MENTION, said out loud in the room it was asked in (npc-llm-layer §3.5 tier 1).
  //
  // Public question, public answer: `@daniel what about the alerts` is addressed in front of everyone,
  // so the reply belongs in the same room rather than in a DM. It renders as an ordinary message from
  // an ordinary colleague, because that is what it is - the only thing that makes it special is that a
  // player aimed a sentence at a character and the character answered.
  for (const line of view?.npcChannelLines ?? []) {
    const who = seatCharacter(game?.seats.find((s) => s.key === line.seat));
    if (!who) {
      // No authored character, no line: a voice with nobody behind it is the §1b failure the whole
      // cast table exists to prevent.
      continue;
    }

    drafts.push({
      id: `mention-${line.forSeq}-${line.seat}`,
      sender: who,
      text: line.text,
      note: null,
      channel: line.channel,
      round: rounds.roundAt(line.forSeq, round),
      // Just after the line it answers, so a room reads as a conversation rather than as two lists.
      order: line.forSeq + 0.5,
      realTime: null,
      kind: 'chat',
      actions: [],
    });
  }

  // THE MILESTONE NOTE (DEC-17 M1), posted by the meeting-notes bot into #standup.
  //
  // A thread and NOT a takeover. The all-hands that seizes the screen is the FINALE's ceremony (§2f) and
  // only its - it works because it happens once, at the end, and a mid-session reflection borrowing it
  // would tell a team the game is over three times before it is. A milestone is a working beat: it
  // arrives in the room, in the bot that already writes up meetings, and the team reads it when they
  // read it.
  //
  // The engine's headline posts immediately; the written note replaces it when the job lands. That is
  // the same two-stage shape the retro has, and it is why a dead provider costs the prose and never the
  // chapter.
  for (const chapter of view?.milestones ?? []) {
    const bot = systemBot('meeting');
    const posts = chapter.posts?.length ? chapter.posts : chapter.text ? [chapter.text] : [];

    drafts.push({
      id: `milestone-${chapter.seq}`,
      sender: bot,
      text: t('milestone.post', {
        gate: milestoneTitleOf(game, chapter.gate),
        headline: chapter.headline,
      }),
      note: null,
      channel: STANDUP,
      round: rounds.roundAt(chapter.seq, round),
      order: chapter.seq,
      seq: chapter.seq,
      realTime: null,
      kind: 'narration',
      actions: [],
    });

    posts.forEach((post, i) => {
      drafts.push({
        id: `milestone-${chapter.seq}-${i}`,
        sender: bot,
        text: post,
        note: null,
        channel: STANDUP,
        round: rounds.roundAt(chapter.seq, round),
        order: chapter.seq + 0.01 + i * 0.001,
        realTime: null,
        kind: 'narration',
        actions: [],
      });
    });
  }

  // The private threads (§2c, step 4). One per NPC-held seat, carrying the answers this player has
  // actually been given - `npcReplies` is a PER-PLAYER slice the server only ever serves to whoever
  // asked, so a DM cannot leak into somebody else's rail by construction.
  // YOUR OWN QUESTIONS (§2c). A thread that shows only her answers is a thread of one person talking:
  // you type, she replies, and the thing you asked is nowhere. The asks are client-local by
  // construction - an ask is not a committed message, only its ANSWER comes back - so they are handed
  // in here the same way the outbox is, and paired with the replies by arrival order.
  // You, as the room draws you - the same human your channel lines carry, so a DM is not rendered by
  // a different rule from a room.
  const me = view && playerId ? senderForPlayer(view, playerId) : null;
  const askedBySeat = new Map<string, { seat: string; text: string }[]>();
  for (const ask of asks) {
    askedBySeat.set(ask.seat, [...(askedBySeat.get(ask.seat) ?? []), ask]);
  }

  const answered = new Map<string, number>();
  const pushAsk = (ask: { seat: string; text: string } | undefined, who: CastMember) => {
    if (!ask) return;
    drafts.push({
      id: `dm-asked-${ask.seat}-${drafts.length}`,
      sender: me ?? who,
      text: ask.text,
      note: null,
      channel: dmOf(ask.seat),
      round,
      order: drafts.length,
      realTime: null,
      kind: 'dm',
      actions: [],
    });
  };

  for (const reply of view?.npcReplies ?? []) {
    const who = seatCharacter(game?.seats.find((s) => s.key === reply.seat));
    if (!who) {
      // No authored character, no DM: an anonymous seat answering privately would be a voice with
      // nobody behind it, which is the §1b failure the whole cast table exists to prevent.
      continue;
    }

    // The question this answer is an answer TO, immediately above it.
    const k = answered.get(reply.seat) ?? 0;
    answered.set(reply.seat, k + 1);
    pushAsk(askedBySeat.get(reply.seat)?.[k], who);

    // The venetian blind (§2c): their own phrase, shown back ABOVE the answer, so the compression is
    // visible rather than hidden behind prose that sounds like it understood everything. The quote is
    // server-verified as a literal substring of what they typed - the client never re-derives it.
    if (reply.evidence) {
      drafts.push({
        id: `dm-heard-${reply.seat}-${drafts.length}`,
        sender: who,
        text: t('dm.heard', { quote: reply.evidence }),
        note: null,
        channel: dmOf(reply.seat),
        round,
        order: drafts.length,
        realTime: null,
        kind: 'narration',
        actions: [],
      });
    }

    drafts.push({
      id: `dm-${reply.seat}-${drafts.length}`,
      sender: who,
      text: reply.text,
      note: reply.source === 'scripted' ? t('dm.scripted') : null,
      channel: dmOf(reply.seat),
      round,
      order: drafts.length + 0.001,
      realTime: null,
      kind: 'dm',
      actions: [],
    });
  }

  // An ask still waiting on its answer. `askingSeats` already draws the "…thinking" indicator; what
  // was missing was the question it is thinking ABOUT, so the thread showed a colleague pondering
  // nothing.
  for (const [seat, list] of askedBySeat) {
    const who = seatCharacter(game?.seats.find((s) => s.key === seat));
    if (!who) {
      continue;
    }

    for (const ask of list.slice(answered.get(seat) ?? 0)) {
      pushAsk(ask, who);
    }
  }

  // THE DECISION THREAD (§2b/§2n). The card is posted by the seat that OWNS the call - "I need an
  // answer on this by Thursday" from a person, not a card from the void - and the arguments sit
  // underneath it, which is the medium doing natively what a bare card never could.
  for (const decision of game?.decisions ?? []) {
    const ownerSeat = game?.seats.find((s) => s.key === decision.owner);
    const sender =
      seatCharacter(ownerSeat) ??
      (ownerSeat?.player ? senderForPlayer(view!, ownerSeat.player) : systemBot('digest'));

    drafts.push({
      id: `decision-${decision.id}`,
      sender,
      text: t('decision.card', {
        kind: decision.kind,
        day: String(workdayOf(decision.dueRound, deadline)),
      }),
      note: styleNote(decision),
      channel: DECISIONS,
      round,
      order: 1000 + drafts.length,
      realTime: null,
      kind: 'incident',
      actions: seated
        ? decisionActions(
            {
              id: decision.id,
              style: decision.style,
              owner: decision.owner,
              options: decision.options,
              consulted: decision.consulted.length,
              requiredConsults: decision.requiredConsults,
              votes: decision.votes,
              quorum: decision.quorum,
              approved: decision.approved,
              approver: decision.approver,
              deferrals: decision.deferrals,
            },
            mySeat?.key ?? null,
          ).map((a) => ({
            id: a.id,
            label: a.label,
            input: a.action,
            ceremony: a.ceremony,
            stakes: [],
            odds: null,
          }))
        : [],
    });

    // The arguments. NPC lines attached to the beat that RAISED this call are the stance job's output
    // (10a item 4b) - the engine computed each character's preference and the voice layer said it out
    // loud, so a solo player deliberates against somebody by default (game-loop §2.3 surviving the
    // skin). Matched by the beat rather than by a marker on the line: the raising beat is the one the
    // engine stamped with `decision-raised`, and nothing else about that beat is guesswork.
    const raised = (view?.feed ?? []).find((f) =>
      f.emitted.some(
        (e) => e.kind === 'decision-raised' && (e.note ?? '').startsWith(`${decision.id}:`),
      ),
    );

    for (const said of raised?.npcLines ?? []) {
      const who = seatCharacter(game?.seats.find((s) => s.key === said.seat));
      if (!who) continue;
      drafts.push({
        id: `stance-${decision.id}-${said.seat}`,
        sender: who,
        text: said.text,
        note: null,
        channel: DECISIONS,
        round,
        order: 1000 + drafts.length,
        realTime: null,
        kind: 'npc-stance',
        actions: [],
      });
    }
  }

  const goal = game?.goal ?? null;
  if (game) {
    const incident = incidentDraft(game, goal, seated);
    if (incident) {
      drafts.push(incident);
    }
    drafts.push(...standupDrafts(game, seated));
  }

  const placed = place(drafts, deadline, view?.reactions ?? {});

  // THE OUTBOX (stage one). Your own unconfirmed gestures, merged in at render time and never in the
  // store - see `core/session/outbox.ts` for why that boundary is load-bearing. Pending LINES go last
  // in their room, because they have no seq and cannot be interleaved with things that do; pending
  // REACTIONS ride the message they point at.
  if (outbox && view) {
    const committedSeq = (seq: number) => view.chat.some((c) => c.seq === seq);

    for (const messages of placed.values()) {
      for (const message of messages) {
        if (message.seq === null) {
          continue;
        }

        const committed = new Set(message.reactions);
        const waiting = outbox.reactionsOn(view.id, message.seq, (r) => committed.has(r));
        if (waiting.length) {
          message.pendingReactions = waiting.map((r) => ({ reaction: r.reaction, state: r.state }));
        }
      }
    }

    // Pending lines are placed over the OUTBOX rather than over the buckets, because a room with
    // nothing committed in it yet HAS no bucket - and that is exactly the room a first line lands in.

    for (const line of outbox.pendingIn(view.id).lines) {
      if (line.seq !== null && line.state === 'sending' && committedSeq(line.seq)) {
        continue; // its committed self is already on screen
      }

      // `null` is the default room in the outbox exactly as it is in the store; this client calls that
      // room TEAM, and the mapping lives here rather than in either of them.
      const channel = line.channel ?? TEAM;
      const messages = placed.get(channel) ?? [];
      messages.push({
        id: `pending:${line.cmdId}`,
        sender: playerId ? senderForPlayer(view, playerId) : systemBot('digest'),
        text: line.text,
        note: null,
        day,
        clock: clockOf(messages.length),
        realTime: null,
        kind: 'chat',
        actions: [],
        // After everything committed: a line with no seq has no place in the sequence.
        order: Number.MAX_SAFE_INTEGER,
        seq: null,
        reactions: [],
        pending: { cmdId: line.cmdId, state: line.state, error: line.error },
      });
      placed.set(channel, messages);
    }
  }
  const sev = game?.meters?.['sev'];
  const alarm = breached(goal);

  const channels: Omit<ChannelView, 'unread' | 'murmur'>[] = [
    {
      id: DM_BUDDY,
      kind: 'dm',
      label: t('channel.dm', { name: t('buddy.name') }),
      topic: null,
      tone: 'normal',
      placeholder: t('composer.dm', { name: t('buddy.name') }),
      emptyText: t('empty.dm'),
      messages: placed.get(DM_BUDDY) ?? [],
      readings: [],
    },
    {
      id: TEAM,
      kind: 'channel',
      label: t('channel.team'),
      topic: t('channel.teamPurpose'),
      tone: 'normal',
      placeholder: t('composer.team'),
      emptyText: t('empty.team'),
      messages: placed.get(TEAM) ?? [],
      readings: [],
    },
  ];

  if (game) {
    // A private thread per colleague (§2c). Present from minute zero rather than conjured by the first
    // question: a messenger where people appear only after you talk to them is a directory, and §1b's
    // whole claim is that these are colleagues who were already here.
    //
    // Ordered after #team so the rail reads rooms-then-people, which is what every messenger does.
    for (const seat of game.seats) {
      const who = seatCharacter(seat);
      if (!who || seat.mine) {
        // No authored character, no thread - and no DM with yourself.
        continue;
      }

      channels.push({
        id: dmOf(seat.key),
        kind: 'dm',
        label: t('channel.dm', { name: who.name }),
        topic: null,
        tone: 'normal',
        placeholder: t('composer.dm', { name: who.name }),
        emptyText: t('empty.dmColleague', { name: who.name }),
        messages: placed.get(dmOf(seat.key)) ?? [],
        readings: [],
      });
    }

    // #random exists once somebody has actually said something in it. A permanently empty watercooler
    // is a room that says this cast has nothing to say off duty, which is the opposite of §2h's point.
    const randomMessages = placed.get(RANDOM) ?? [];
    if (randomMessages.length) {
      channels.push({
        id: RANDOM,
        kind: 'channel',
        label: t('channel.random'),
        topic: t('channel.randomPurpose'),
        tone: 'normal',
        placeholder: t('composer.random'),
        emptyText: t('empty.random'),
        messages: randomMessages,
        readings: [],
      });
    }

    // #decisions exists while something is actually waiting on the team. An empty decisions room is a
    // reminder that nobody has to decide anything, which is the opposite of the pressure it carries.
    const decisionMessages = placed.get(DECISIONS) ?? [];
    if (decisionMessages.length) {
      channels.push({
        id: DECISIONS,
        kind: 'channel',
        label: t('channel.decisions'),
        topic: t('channel.decisionsPurpose'),
        tone: 'normal',
        placeholder: t('composer.decisions'),
        emptyText: t('empty.decisions'),
        messages: decisionMessages,
        readings: [],
      });
    }

    // #standup exists only once there is a meeting to hold. A permanently empty meeting room is a
    // reminder that nobody met, which is the opposite of what the Offered contract is for.
    //
    // A milestone note counts as a meeting (DEC-17 M1): it is the meeting-notes bot writing up a stretch
    // of work, which is the most meeting-shaped thing in the game. So a session that crossed a gate has
    // a #standup even if it never held a standup.
    const standupMessages = placed.get(STANDUP) ?? [];
    if (standupMessages.length) {
      channels.push({
        id: STANDUP,
        kind: 'channel',
        label: t('channel.standup'),
        topic: game.scene?.offered ? t('channel.standupOffered') : t('channel.standupPurpose'),
        tone: 'normal',
        placeholder: t('composer.standup'),
        emptyText: t('empty.standup'),
        messages: standupMessages,
        readings: [],
      });
    }

    channels.push({
      id: WAR_ROOM,
      kind: 'channel',
      // The severity sits IN the channel name (§2d): a room whose title changes as it gets worse is
      // the cheapest dread this medium sells.
      label:
        typeof sev === 'number' && sev > 0
          ? t('channel.warRoomSev', { n: sev })
          : t('channel.warRoom'),
      topic: topicLine(goal, day) ?? t('channel.warRoomPurpose'),
      tone: alarm ? 'alarm' : 'normal',
      placeholder: t('composer.warRoom'),
      emptyText: t('empty.warRoom'),
      messages: placed.get(WAR_ROOM) ?? [],
      readings: [],
    });

    // Per-meter diegesis (§2o): each meter wears the fiction it actually has in a company, and a page
    // with nothing to say is simply absent. There is no god-dashboard charting feelings.
    for (const [id, label, lead] of [
      [PAGE_MONITORING, t('page.monitoring'), t('page.monitoringLead')],
      [PAGE_PULSE, t('page.pulse'), t('page.pulseLead')],
      [PAGE_ACCOUNTS, t('page.accounts'), t('page.accountsLead')],
    ] as const) {
      const readings = readingsFor(game, id);
      if (readings.length === 0) {
        continue;
      }

      channels.push({
        id,
        kind: 'page',
        label,
        topic: lead,
        tone: readings.some((r) => r.tone === 'bad') ? 'alarm' : 'normal',
        placeholder: null,
        emptyText: t('page.empty'),
        messages: [],
        readings,
      });
    }
  }

  return {
    name,
    round,
    finale: finaleOf(view, game, mySeat?.key ?? null),
    lobby: Boolean(game) && !started,
    started,
    day,
    channels: channels.map((c) => withCorridor(c, marks)),
    calendar: calendarNote(game),
    milestone: milestoneNote(game),
    typing: typingRows(view, game),
    members: membersOf(view, game, playerId),
    you: { seat: mySeat?.key ?? null, signedOff, verbs: mySeat?.powers ?? [] },
  };
}

/**
 * The corridor (§2a): what has landed here since you last looked, and the first line of it.
 *
 * A channel this player has NEVER opened counts as fully read rather than fully unread. The alternative
 * is a workspace that greets a newcomer with a badge on every room - which is not awareness, it is a
 * chore list, and it makes the one badge that matters invisible.
 */
function withCorridor(
  channel: Omit<ChannelView, 'unread' | 'murmur'>,
  marks: ReadMarks,
): ChannelView {
  const mark = marks.markOf(channel.id);
  if (mark === null || channel.messages.length === 0) {
    return { ...channel, unread: 0, murmur: null };
  }

  const fresh = channel.messages.filter((m) => m.order > mark);
  const newest = fresh[fresh.length - 1];
  return {
    ...channel,
    unread: fresh.length,
    // One line, and only the first of it: a murmur is something you catch out of the corner of an eye,
    // not a second copy of the room.
    murmur: newest ? `${newest.sender.name}: ${firstLine(newest.text)}` : null,
  };
}

function firstLine(text: string): string {
  const line = text.split('\n')[0] ?? '';
  return line.length > 60 ? `${line.slice(0, 59)}…` : line;
}

/**
 * The calendar strip (§2a): the engine's own answer to "what is coming", rendered as the reminder a
 * workplace calendar gives. Null when the engine knows of nothing - the calendar never invents, so
 * "nothing scheduled" is silence rather than a line saying so.
 */
function calendarNote(game: GameStatePayload | null): CalendarNote | null {
  const upcoming = game?.upcoming;
  if (!upcoming) {
    return null;
  }

  const scene = sceneName(upcoming.scene);
  return upcoming.inRounds === 0
    ? { text: t('calendar.today', { scene }), imminent: true }
    : upcoming.inRounds === 1
      ? { text: t('calendar.tomorrow', { scene }), imminent: false }
      : { text: t('calendar.later', { scene, days: upcoming.inRounds }), imminent: false };
}

/**
 * The milestone chain as the workspace reads it (DEC-17 M1).
 *
 * Null on a deck with a single stretch and no gates: this client's rule is that it never renders a
 * reminder about nothing, and "you are in the only phase there is" is exactly that. The unlocks travel
 * so a ceremony the team cannot convene yet can say WHEN instead of just refusing - a governance meeting
 * that is greyed out with no explanation is a bug report, and one that says "opens at Renew" is a plan.
 */
/**
 * A gate's authored title, falling back to its key (DEC-17 M1). The note names the milestone the way the
 * deck named it - "Resolve", not `resolve` - and a client that cannot find the link says the key rather
 * than inventing a word for it.
 */
function milestoneTitleOf(game: GameStatePayload | null, gate: string): string {
  return game?.milestone?.chain.find((l) => l.name === gate)?.title ?? gate;
}

function milestoneNote(game: GameStatePayload | null): MilestoneNote | null {
  const milestone = game?.milestone;
  if (!milestone || milestone.chain.length < 2) {
    return null;
  }

  const current = milestone.title ?? milestone.name ?? milestone.chain[milestone.index]?.name ?? '';
  const next = milestone.chain[milestone.index + 1] ?? null;
  const advance = game?.goal?.advance ?? null;
  return {
    current,
    next: next ? (next.title ?? next.name) : null,
    // Through the SAME readout the goal line and the Monitoring page use (§2g's language law lives in
    // one place), so the gate reads in the cast's words rather than as an expression.
    nextNeeds: next && advance ? conditionLine(advance.condition, false) : null,
    unlocks: next?.unlocks ?? [],
  };
}

/**
 * The member list: colleagues, never vacancies (§1b). A bot-filled seat is Daniel, not an empty chair
 * with a sign - the "open role" affordance only appears while the room is still gathering.
 *
 * Presence dots are LIVE HUMANS ONLY. An NPC has no dot, because faking one would be the theatre §1b
 * forbids, and a human who drifted gets no headstone: their seat simply shows the character again.
 */
function membersOf(
  view: SessionView | undefined,
  game: GameStatePayload | null,
  playerId: string | null,
): MemberRow[] {
  if (!game) {
    // No board: the room is the roster. Everyone here is a person who turned up.
    return (view?.members ?? [])
      .filter((m) => m.state !== 'left')
      .map((m) => ({
        id: m.playerId,
        who: human(m.playerId, nameOf(view!, m.playerId)),
        role: '',
        presence: presenceOf(view, m.playerId),
        mine: m.playerId === playerId,
        claimable: false,
        does: [],
        pitch: null,
        recommended: false,
      }));
  }

  return game.seats.map((seat) => {
    const holder = seat.occupant === 'human' && seat.player ? seat.player : null;
    // Who this row is, in order of what is true: the human holding it, the character the scenario
    // authored for it, or - failing both - the JOB. Not "Open role": during play that is a vacancy
    // sign on a chair, which is the one thing §1b forbids ("nothing may read as nobody wanted to play
    // with you"). A job that exists reads as a team that has one, which is also simply the case.
    const who = holder
      ? human(holder, nameOf(view!, holder))
      : (seatCharacter(seat) ?? {
          id: `role:${seat.key}`,
          name: roleName(seat.key, seat.title),
          avatar: null,
          kind: 'person' as const,
          subtitle: null,
        });

    return {
      id: `seat:${seat.key}`,
      who,
      role: seat.key,
      presence: holder ? presenceOf(view, holder) : null,
      mine: seat.mine,
      // DEC-16 was already satisfied here by §1b: taking a role belongs to the room while it is
      // forming, and during play a bot-held seat is a colleague rather than an empty chair.
      //
      // DEC-19 REOPENS IT, and the reason is the playtest rather than a change of aesthetic: a person
      // with no role mid-day had no path to one and nothing said so. `affordancesFor` still shows this
      // to NOBODY who already holds a role, so what §1b was protecting - a seated table not being
      // offered its colleagues' jobs - is still protected. What changes is that a person watching from
      // outside can join in, which is the whole of the owner's ruling.
      claimable: seat.claimable,
      does: seat.powers,
      // The deck's own pitch and its own recommendation, for the "who are you?" picker.
      pitch: seat.pitch ?? null,
      recommended: seat.recommended ?? false,
    };
  });
}

/**
 * Who the server says is composing. Cast through the same table everything else is (§2n) - a typing
 * indicator for a seat the scenario never named would be an anonymous ghost, so it is dropped, exactly
 * as an unnamed NPC's line is.
 */
function typingRows(view: SessionView | undefined, game: GameStatePayload | null): TypingRow[] {
  const rows: TypingRow[] = [];
  for (const [seat, isTyping] of Object.entries(view?.typing ?? {})) {
    if (!isTyping) continue;
    const who = seatCharacter(game?.seats.find((s) => s.key === seat));
    if (who) {
      rows.push({ who, channel: WAR_ROOM });
    }
  }

  return rows;
}

function presenceOf(view: SessionView | undefined, playerId: string): 'here' | 'away' | 'gone' {
  // Presence is keyed by the FULL id (it rides `evt.presence.changed`), and a seat hands us the short
  // one. Resolve through the roster first, or every seat's dot would read "here" by default and mean
  // nothing at all.
  const full = view ? (memberOf(view, playerId)?.playerId ?? playerId) : playerId;
  const state = view?.presence[full];
  if (state === 'offline') {
    return 'gone';
  }

  return state === 'away' ? 'away' : 'here';
}
